import { canUseLocalStorage } from "@/utils/canuseLocalStorage";
import { creationProductManager } from "./creation-product-manager";

/**
 * 最后访问创作区块路由管理器
 * 负责管理用户最后访问的创作区块路由，提供缓存、获取、跳转等功能
 * @author 杨俊朗
 */
export class RouteLastVisitCreationManager {
  private static instance: RouteLastVisitCreationManager;
  private readonly STORAGE_KEY = "MIUTA_LAST_VISIT_CREATION";
  private readonly DEFAULT_ROUTE = "/creation/folder/uncategorized/";
  private store: string = "";

  private constructor() {
    this.init();
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): RouteLastVisitCreationManager {
    if (!RouteLastVisitCreationManager.instance) {
      RouteLastVisitCreationManager.instance = new RouteLastVisitCreationManager();
    }
    return RouteLastVisitCreationManager.instance;
  }

  /**
   * 初始化 - 从 localStorage 读取最后访问路由
   */
  private init(): void {
    if (canUseLocalStorage()) {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      try {
        this.store = stored ? stored : this.DEFAULT_ROUTE;
      } catch (error) {
        console.error(
          "Failed to parse last visit route from localStorage:",
          error
        );
        this.store = this.DEFAULT_ROUTE;
      }
    }
  }

  /**
   * 更新最后访问路由
   * @param route 路由路径
   */
  public update(route: string): void {
    // 需要缓存的访问路径
    if (!route.startsWith("/creation/")) {
      return;
    }
    console.log("update last visit route", route, import.meta.url);
    this.store = route;

    // 存储到 localStorage
    if (canUseLocalStorage()) {
      if (route) {
        localStorage.setItem(this.STORAGE_KEY, route);
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    }
  }

  /**
   * 解析路径类型和ID
   * @param route 路由路径
   * @returns 解析结果
   */
  private parseRoute(route: string): {
    type: 'folder' | 'project' | 'other';
    id?: string;
    hasTrailingSlash: boolean;
  } {
    // 标准化路径，确保以斜杠开头
    const normalizedRoute = route.startsWith('/') ? route : `/${route}`;

    // 匹配 /creation/folder/ 或 /creation/folder/:id/ 或 /creation/folder/:id
    const folderMatch = normalizedRoute.match(/^\/creation\/folder\/(([^/]+)\/?)?$/);
    if (folderMatch) {
      return {
        type: 'folder',
        id: folderMatch[2] || undefined, // 提取ID（如果存在）
        hasTrailingSlash: normalizedRoute.endsWith('/')
      };
    }

    // 匹配 /creation/project/ 或 /creation/project/:id/ 或 /creation/project/:id
    const projectMatch = normalizedRoute.match(/^\/creation\/project\/(([^/]+)\/?)?$/);
    if (projectMatch) {
      return {
        type: 'project',
        id: projectMatch[2] || undefined, // 提取ID（如果存在）
        hasTrailingSlash: normalizedRoute.endsWith('/')
      };
    }

    return {
      type: 'other',
      hasTrailingSlash: normalizedRoute.endsWith('/')
    };
  }

  /**
   * 获取最后访问路由
   */
  public async get(): Promise<string> {
    // 解析当前存储的路径
    const routeInfo = this.parseRoute(this.store);
    // 访问创作区块需要初始化创作产品数据
    if (this.store.startsWith("/creation/")) {
      await creationProductManager.init();
    }
    // 访问的是动态路径
    if (routeInfo.type === 'folder' || routeInfo.type === 'project') {
      // // 文件夹路径 检查文件夹是否存在 存在则允许跳转
      // if (routeInfo.type === 'folder') {
      //   const folderOriginIdMap = this.creationProductManager.getFolderOriginIdMap();
      //   const folderPrefix = this.creationProductManager.getFolderPrefix();
      //   if (folderOriginIdMap[`${folderPrefix}${routeInfo.id}`]) {
      //     return this.store;
      //   }
      // }
      // // 项目路径 检查项目是否存在 存在则允许跳转
      if (routeInfo.type === 'project') {
        const projectOriginIdMap = creationProductManager.getProjectOriginIdMap();
        const projectPrefix = creationProductManager.getProjectPrefix();
        if (projectOriginIdMap[`${projectPrefix}${routeInfo.id}`]) {
          return this.store;
        }
      }
      // // 跳转第一个文件或第一个文件夹
      // const folders = this.creationProductManager.getFolders();
      // if (folders.length > 0) {
      //   const folder = folders[0];
      //   const projectsMap = this.creationProductManager.getProjectsMap();
      //   if (projectsMap[folder.id].length > 0) {
      //     const project = projectsMap[folder.id][0];
      //     const projectOriginIdMap = this.creationProductManager.getProjectOriginIdMap();
      //     const projectId = projectOriginIdMap[project.id];
      //     if (projectId) {
      //       this.creationProductManager.toggleFolderExpand(folder.id, true);
      //       return `/creation/project/${projectId}/`;
      //     }
      //   }
      //   const folderOriginIdMap = this.creationProductManager.getFolderOriginIdMap();
      //   const folderId = folderOriginIdMap[folder.id];
      //   if (folderId) {
      //     return `/creation/folder/${folderId}/`;
      //   }
      // }
      // 跳转未分类第一个文件
      const projectsMap = creationProductManager.getProjectsMap();
      const uncategorizedProjects = projectsMap[creationProductManager.getUncategorized()];
      if (uncategorizedProjects.length > 0) {
        const project = uncategorizedProjects[0];
        const projectOriginIdMap = creationProductManager.getProjectOriginIdMap();
        const projectId = projectOriginIdMap[project.id];
        if (projectId) {
          return `/creation/project/${projectId}/`;
        }
      }
      // 跳转默认路由
      return this.DEFAULT_ROUTE;
    }
    // 其他路径
    return this.store || this.DEFAULT_ROUTE;
  }

  /**
   * 获取默认路由
   */
  public getDefault(): string {
    return this.DEFAULT_ROUTE;
  }

  /**
   * 清除最后访问路由
   */
  public clear(): void {
    this.update("");
  }
}

// 导出单例实例
export const routeLastVisitCreationManager = RouteLastVisitCreationManager.getInstance();
